import { RequireModule } from "@/hooks/useModules";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { enforceBrandingScope } from "@/lib/brandingScope";
import { ProtectedRoute } from "./ProtectedRoute";
import { PublicRoute } from "./PublicRoute";
import { ROUTES } from "./routeConfig";
import { BrandedLoader } from "@/components/shared/BrandedLoader";

import LoginPage from "@/auth/LoginPage";


// Super Admin


// Institution Admin

import NotFoundPage from "@/components/shared/NotFoundPage";

// Org Admin

// Campus Admin
import { Banknote, Library } from "lucide-react";


/** Strict branding: platform routes (Super Admin, login, …) always get the platform brand; see lib/brandingScope.ts. */
function BrandingScopeGuard() {
  const { pathname } = useLocation();
  useEffect(() => {
    enforceBrandingScope(pathname);
  }, [pathname]);
  return null;
}

/**
 * Catch-all path for one panel, taken from that panel's dashboard path: "/institution-admin/dashboard" gives "/institution-admin/*".
 * It has to carry the panel prefix: a bare "*" inside a layout would match every unknown URL in the app, whichever panel it belongs to.
 */
const panelCatchAll = (dashboardPath: string) => `/${dashboardPath.split("/")[1]}/*`;


// Pages load when first opened, so the first screen does not download the whole app.
const LandingPage = lazy(() => import("@/landing/pages/LandingPage"));
const BlogPage = lazy(() => import("@/landing/pages/BlogPage"));
const TeacherPortalLayout = lazy(() => import("@/modules/teacherPortal/layout/TeacherPortalLayout"));
const TeacherHomePage = lazy(() => import("@/modules/teacherPortal/pages/HomePage"));
const TeacherTimetablePage = lazy(() => import("@/modules/teacherPortal/pages/TimetablePage"));
const TeacherAttendancePage = lazy(() => import("@/modules/teacherPortal/pages/AttendancePage"));
const TeacherMarksPage = lazy(() => import("@/modules/teacherPortal/pages/MarksPage"));
const TeacherStaffMeetingsPage = lazy(() => import("@/modules/teacherPortal/pages/StaffMeetingsPage"));
const StaffMeetingsPage = lazy(() => import("@/modules/institutionAdmin/staffMeetings/pages/StaffMeetingsPage"));
const TeacherStudentLeavePage = lazy(() => import("@/modules/teacherPortal/pages/StudentLeavePage"));
const AdminStudentJourneyPage = lazy(() => import("@/modules/institutionAdmin/studentJourney/pages/StudentJourneyPage"));
const StudentLeavePage = lazy(() => import("@/modules/institutionAdmin/studentLeave/pages/StudentLeavePage"));
const StudentPortalLeavePage = lazy(() => import("@/modules/studentPortal/leave/pages/LeavePage"));
const TeacherStudentJourneyPage = lazy(() => import("@/modules/teacherPortal/pages/StudentJourneyPage"));
const TeacherClassDiaryPage = lazy(() => import("@/modules/teacherPortal/pages/ClassDiaryPage"));
const ClassDiaryPage = lazy(() => import("@/modules/institutionAdmin/classDiary/pages/ClassDiaryPage"));
const StudentDiaryPage = lazy(() => import("@/modules/studentPortal/diary/pages/DiaryPage"));
const TeacherLessonPlansPage = lazy(() => import("@/modules/teacherPortal/pages/LessonPlansPage"));
const LessonPlansPage = lazy(() => import("@/modules/institutionAdmin/lessonPlans/pages/LessonPlansPage"));
const TeacherOnlineClassesPage = lazy(() => import("@/modules/teacherPortal/pages/OnlineClassesPage"));
const StudentOnlineClassesPage = lazy(() => import("@/modules/studentPortal/onlineLearning/pages/OnlineClassesPage"));
const OnlineLearningPage = lazy(() => import("@/modules/institutionAdmin/onlineLearning/pages/OnlineLearningPage"));
const TeacherHomeworkPage = lazy(() => import("@/modules/teacherPortal/pages/HomeworkPage"));
const TeacherNotesPage = lazy(() => import("@/modules/teacherPortal/pages/NotesPage"));
const TeacherProfilePage = lazy(() => import("@/modules/teacherPortal/pages/ProfilePage"));
const StudentPortalLayout = lazy(() => import("@/modules/studentPortal/layout/StudentPortalLayout"));
const StudentHomePage = lazy(() => import("@/modules/studentPortal/dashboard/pages/HomePage"));
const StudentTimetablePage = lazy(() => import("@/modules/studentPortal/timetable/pages/TimetablePage"));
const StudentAttendancePage = lazy(() => import("@/modules/studentPortal/attendance/pages/AttendancePage"));
const StudentFeesPage = lazy(() => import("@/modules/studentPortal/fees/pages/FeesPage"));
const StudentResultsPage = lazy(() => import("@/modules/studentPortal/results/pages/ResultsPage"));
const StudentExamsPage = lazy(() => import("@/modules/studentPortal/exams/pages/ExamsPage"));
const StudentHomeworkPage = lazy(() => import("@/modules/studentPortal/homework/pages/HomeworkPage"));
const StudentNotesPage = lazy(() => import("@/modules/studentPortal/notes/pages/NotesPage"));
const StudentProfilePage = lazy(() => import("@/modules/studentPortal/profile/pages/ProfilePage"));
const ExamsPage = lazy(() => import("@/modules/institutionAdmin/exams/pages/ExamsPage"));
const ExamDetailPage = lazy(() => import("@/modules/institutionAdmin/exams/pages/ExamDetailPage"));
const HomeworkPage = lazy(() => import("@/modules/institutionAdmin/homework/pages/HomeworkPage"));
const NotesPage = lazy(() => import("@/modules/institutionAdmin/notes/pages/NotesPage"));
const MasterSyllabusPage = lazy(() => import("@/modules/institutionAdmin/syllabus/pages/MasterSyllabusPage"));
const TeacherSyllabusPage = lazy(() => import("@/modules/teacherPortal/pages/SyllabusPage"));
const StudentSyllabusPage = lazy(() => import("@/modules/studentPortal/syllabus/pages/SyllabusPage"));
const NoticesPage = lazy(() => import("@/modules/institutionAdmin/notices/pages/NoticesPage"));
const CertificatesPage = lazy(() => import("@/modules/institutionAdmin/certificates/pages/CertificatesPage"));
const CertificatePublicPage = lazy(() => import("@/modules/institutionAdmin/certificates/pages/CertificatePublicPage"));
const CertificatePrintPage = lazy(() => import("@/modules/institutionAdmin/certificates/pages/CertificatePrintPage"));
const NoticePrintPage = lazy(() => import("@/modules/institutionAdmin/notices/pages/NoticePrintPage"));
const PrivacyPolicyPage = lazy(() => import("@/landing/pages/LegalPage").then((m) => ({ default: m.PrivacyPolicyPage })));
const RefundPolicyPage = lazy(() => import("@/landing/pages/LegalPage").then((m) => ({ default: m.RefundPolicyPage })));
const TermsPage = lazy(() => import("@/landing/pages/LegalPage").then((m) => ({ default: m.TermsPage })));
const BlogDetailPage = lazy(() => import("@/landing/pages/BlogDetailPage"));
const ApplyPage = lazy(() => import("@/modules/publicAdmission/pages/ApplyPage"));
const VerifyStudentPage = lazy(() => import("@/modules/publicVerify/pages/VerifyStudentPage"));
const SuperAdminLayout = lazy(() => import("@/modules/superAdmin/layout/SuperAdminLayout").then((m) => ({ default: m.SuperAdminLayout })));
const DashboardPage = lazy(() => import("@/modules/superAdmin/dashboard/pages/DashboardPage"));
const LeadTrackerPage = lazy(() => import("@/modules/superAdmin/leadTracker/pages/LeadTrackerPage"));
const InternalRolesPage = lazy(() => import("@/modules/superAdmin/internalRoles/pages/InternalRolesPage"));
const VisitorPreRegisterPage = lazy(() => import("@/modules/publicVisitor/pages/VisitorPreRegisterPage"));
const AdmissionQrDisplayPage = lazy(() => import("@/modules/publicAdmission/pages/AdmissionQrDisplayPage"));
const VisitorQrDisplayPage = lazy(() => import("@/modules/publicVisitor/pages/VisitorQrDisplayPage"));
const StaffPage = lazy(() => import("@/modules/superAdmin/staff/pages/StaffPage"));
const OrgManagementPage = lazy(() => import("@/modules/superAdmin/orgManagement/pages/OrgManagementPage"));
const OrganizationDetailsPage = lazy(() => import("@/modules/superAdmin/orgManagement/pages/OrganizationDetailsPage"));
const CampusDetailsPage = lazy(() => import("@/modules/superAdmin/orgManagement/pages/CampusDetailsPage"));
const IndependentInstitutionDetailsPage = lazy(() => import("@/modules/superAdmin/orgManagement/pages/IndependentInstitutionDetail").then((m) => ({ default: m.IndependentInstitutionDetailsPage })));
const AcademicSetupPage = lazy(() => import("@/modules/superAdmin/academicSetup/pages/AcademicSetupPage"));
const AuditLogsPage = lazy(() => import("@/modules/superAdmin/auditLogs/pages/AuditLogsPage"));
const SubscriptionHistoryPage = lazy(() => import("@/modules/superAdmin/subscriptionHistory/pages/SubscriptionHistoryPage"));
const LoginActivityPage = lazy(() => import("@/modules/superAdmin/loginActivity/pages/LoginActivityPage"));
const ErrorLogsPage = lazy(() => import("@/modules/superAdmin/errorLogs/pages/ErrorLogsPage"));
const StoragePage = lazy(() => import("@/modules/superAdmin/storage/pages/StoragePage"));
const StorageDetailsPage = lazy(() => import("@/modules/superAdmin/storage/pages/StorageDetailsPage"));
const AcademicYearsPage = lazy(() => import("@/modules/superAdmin/academicYears/pages/AcademicYearsPage"));
const AdmissionFormsPage = lazy(() => import("@/modules/superAdmin/admissionForms/pages/AdmissionFormsPage"));
const ReligionsPage = lazy(() => import("@/modules/superAdmin/religions/pages/ReligionsPage"));
const SignatureRolesPage = lazy(() => import("@/modules/superAdmin/signatureRoles/pages/SignatureRolesPage"));
const ReportTemplatesPage = lazy(() => import("@/modules/superAdmin/reportTemplates/pages/ReportTemplatesPage"));
const HeroPage = lazy(() => import("@/modules/superAdmin/cms/hero/pages/HeroPage"));
const ChallengesPage = lazy(() => import("@/modules/superAdmin/cms/challenges/pages/ChallengesPage"));
const PricingPage = lazy(() => import("@/modules/superAdmin/cms/pricing/pages/PricingPage"));
const TestimonialsPage = lazy(() => import("@/modules/superAdmin/cms/testimonials/pages/TestimonialsPage"));
const FaqPage = lazy(() => import("@/modules/superAdmin/cms/faq/pages/FaqPage"));
const SocialLinksPage = lazy(() => import("@/modules/superAdmin/cms/socialLinks/pages/SocialLinksPage"));
const Blog = lazy(() => import("@/modules/superAdmin/cms/blog/pages/BlogPage"));
const CmsPages = lazy(() => import("@/modules/superAdmin/cms/pages/pages/PagesPage"));
const CmsPageView = lazy(() => import("@/landing/pages/CmsPageView"));
const CmsPagesList = lazy(() => import("@/landing/pages/CmsPagesList"));
const InstitutionAdminLayout = lazy(() => import("@/modules/institutionAdmin/layout/InstitutionAdminLayout").then((m) => ({ default: m.InstitutionAdminLayout })));
const InstitutionAdminDashboardPage = lazy(() => import("@/modules/institutionAdmin/dashboard/pages/DashboardPage"));
const ComingSoon = lazy(() => import("@/components/shared/ComingSoon"));
const OrgAdminLayout = lazy(() => import("@/modules/orgAdmin/layout/OrgAdminLayout").then((m) => ({ default: m.OrgAdminLayout })));
const OrgAdminDashboardPage = lazy(() => import("@/modules/orgAdmin/dashboard/pages/DashboardPage"));
const OrgAdminBillingPage = lazy(() => import("@/modules/orgAdmin/billing/pages/BillingPage"));
const OrgAdminSharedStaffPage = lazy(() => import("@/modules/orgAdmin/staff/pages/SharedStaffPage"));
const CampusAdminSharedStaffPage = lazy(() => import("@/modules/campusAdmin/staff/pages/SharedStaffPage"));
const InstitutionAdminBillingPage = lazy(() => import("@/modules/institutionAdmin/billing/pages/BillingPage"));
const StaffPortalPage = lazy(() => import("@/modules/staffPortal/pages/StaffPortalPage"));
const CampusAdminLayout = lazy(() => import("@/modules/campusAdmin/layout/CampusAdminLayout").then((m) => ({ default: m.CampusAdminLayout })));
const CampusAdminDashboardPage = lazy(() => import("@/modules/campusAdmin/dashboard/pages/DashboardPage"));
const SubjectsPage = lazy(() => import("@/modules/superAdmin/subjects/pages/SubjectsPage"));
const FeeTemplatesPage = lazy(() => import("@/modules/superAdmin/feeTemplates/pages/FeeTemplatesPage"));
const WhatsappPage = lazy(() => import("@/modules/superAdmin/whatsapp/pages/WhatsappPage"));
const AcademicsPage = lazy(() => import("@/modules/institutionAdmin/academics/pages/AcademicsPage"));
const SubjectsCatalogPage = lazy(() => import("@/modules/institutionAdmin/academics/pages/SubjectsCatalogPage"));
const TimetablePage = lazy(() => import("@/modules/institutionAdmin/timetable/pages/TimetablePage"));
const CurriculumHubPage = lazy(() => import("@/modules/institutionAdmin/academics/pages/CurriculumHubPage"));
const ClassTeachersPage = lazy(() => import("@/modules/institutionAdmin/academics/pages/ClassTeachersPage"));
const CurriculumFrame = lazy(() => import("@/modules/institutionAdmin/academics/components/CurriculumNav").then((m) => ({ default: m.CurriculumFrame })));
const GenerateTimetablePage = lazy(() => import("@/modules/institutionAdmin/timetable/pages/GenerateTimetablePage"));
const IdCardsPage = lazy(() => import("@/modules/institutionAdmin/idCards/pages/IdCardsPage"));
const StaffIdCardsPage = lazy(() => import("@/modules/institutionAdmin/idCards/pages/StaffIdCardsPage"));
const IdCardPrintPage = lazy(() => import("@/modules/institutionAdmin/idCards/pages/IdCardPrintPage"));
const StaffPageWrapper = lazy(() => import("@/modules/institutionAdmin/staff/components/StaffPageWrapper").then((m) => ({ default: m.StaffPageWrapper })));
const TeacherManagementPage = lazy(() => import("@/modules/institutionAdmin/teachers/pages/TeacherManagementPage"));
const DriverManagementPage = lazy(() => import("@/modules/institutionAdmin/staff/pages/DriverManagementPage"));
const FrontDeskManagementPage = lazy(() => import("@/modules/institutionAdmin/facilities/visitors/pages/FrontDeskManagementPage"));
const RoleStaffPage = lazy(() => import("@/modules/institutionAdmin/staff/pages/RoleStaffPage").then((m) => ({ default: m.RoleStaffPage })));
const StaffDetailPage = lazy(() => import("@/modules/institutionAdmin/staff/pages/StaffDetailPage"));
const FeesPage = lazy(() => import("@/modules/institutionAdmin/fees/pages/FeesPage"));
const InstitutionOldDuesPage = lazy(() => import("@/modules/institutionAdmin/oldDues/OldDuesPage"));
const StudentFeeDeskPage = lazy(() => import("@/modules/institutionAdmin/fees/pages/StudentFeeDeskPage"));
const InstitutionRecycleBinPage = lazy(() => import("@/modules/superAdmin/orgManagement/pages/InstitutionRecycleBinPage"));
const FinancePage = lazy(() => import("@/modules/institutionAdmin/finance/pages/FinancePage"));
const PayrollWorkspacePage = lazy(() => import("@/modules/institutionAdmin/payroll/pages/PayrollWorkspacePage"));
const FeeReceiptPage = lazy(() => import("@/modules/institutionAdmin/fees/pages/FeeReceiptPage"));
const TimetablePrintPage = lazy(() => import("@/modules/institutionAdmin/timetable/pages/TimetablePrintPage"));
const ExamResultPrintPage = lazy(() => import("@/modules/institutionAdmin/exams/pages/ExamResultPrintPage"));
const ExamTimetablePrintPage = lazy(() => import("@/modules/institutionAdmin/exams/pages/ExamTimetablePrintPage"));
const SyllabusPrintPage = lazy(() => import("@/modules/institutionAdmin/syllabus/pages/SyllabusPrintPage"));
const InstitutionSettingsPage = lazy(() => import("@/modules/institutionAdmin/settings/pages/InstitutionSettingsPage"));
const InquiriesPage = lazy(() => import("@/modules/institutionAdmin/admissions/pages/InquiriesPage"));
const AdmissionFormPage = lazy(() => import("@/modules/institutionAdmin/admissions/pages/AdmissionFormPage"));
const EnrollmentHubPage = lazy(() => import("@/modules/institutionAdmin/admissions/pages/EnrollmentHubPage"));
const PromotionsPage = lazy(() => import("@/modules/institutionAdmin/promotions/pages/PromotionsPage"));
const AlumniPage = lazy(() => import("@/modules/institutionAdmin/alumni/pages/AlumniPage"));
const AttendancePage = lazy(() => import("@/modules/institutionAdmin/attendance/pages/AttendancePage"));
const LeavePage = lazy(() => import("@/modules/institutionAdmin/leave/pages/LeavePage"));
const StaffAttendancePage = lazy(() => import("@/modules/institutionAdmin/staffAttendance/pages/StaffAttendancePage"));
const LibraryPage = lazy(() => import("@/modules/institutionAdmin/facilities/library/pages/LibraryPage"));
const TransportPage = lazy(() => import("@/modules/institutionAdmin/facilities/transport/pages/TransportPage"));
const VisitorsPage = lazy(() => import("@/modules/institutionAdmin/facilities/visitors/pages/VisitorsPage"));
const StorePage = lazy(() => import("@/modules/institutionAdmin/facilities/store/pages/StorePage"));
const HostelPage = lazy(() => import("@/modules/institutionAdmin/facilities/hostel/pages/HostelPage"));
const InventoryPage = lazy(() => import("@/modules/institutionAdmin/facilities/inventory/pages/InventoryPage"));
const StudentsPage = lazy(() => import("@/modules/institutionAdmin/students/pages/StudentsPage"));
const CommunicationHubPage = lazy(() => import("@/modules/institutionAdmin/communication/pages/CommunicationHubPage"));
const CommunicationSubNav = lazy(() => import("@/modules/institutionAdmin/communication/components/CommunicationNav").then((m) => ({ default: m.CommunicationSubNav })));
const TeachingHubPage = lazy(() => import("@/modules/institutionAdmin/teachingLearning/pages/TeachingHubPage"));
const WithTeachingNav = lazy(() => import("@/modules/institutionAdmin/teachingLearning/components/TeachingNav").then((m) => ({ default: m.WithTeachingNav })));
const StudentsHubPage = lazy(() => import("@/modules/institutionAdmin/students/pages/StudentsHubPage"));
const StaffHubPage = lazy(() => import("@/modules/institutionAdmin/staff/pages/StaffHubPage"));
const RolesPage = lazy(() => import("@/modules/institutionAdmin/roles/pages/RolesPage"));
const FacilitiesHubPage = lazy(() => import("@/modules/institutionAdmin/facilities/pages/FacilitiesHubPage"));
const WithStudentsNav = lazy(() => import("@/modules/institutionAdmin/students/components/StudentsNav").then((m) => ({ default: m.WithStudentsNav })));
const StudentDetailPage = lazy(() => import("@/modules/institutionAdmin/students/pages/StudentDetailPage"));
const StudentCategoriesPage = lazy(() => import("@/modules/superAdmin/studentCategories/pages/StudentCategoriesPage"));

export function AppRoutes() {
  return (
    <BrowserRouter>
      <BrandingScopeGuard />
      <Suspense fallback={<BrandedLoader />}>
      <Routes>
        <Route element={<PublicRoute />}>
          <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        </Route>

        <Route path={ROUTES.LANDING} element={<LandingPage />} />
        <Route path={ROUTES.BLOG} element={<BlogPage />} />
        <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
        <Route path={ROUTES.REFUND_POLICY} element={<RefundPolicyPage />} />
        <Route path={ROUTES.TERMS} element={<TermsPage />} />
        <Route path={ROUTES.BLOG_DETAIL} element={<BlogDetailPage />} />
        <Route path={ROUTES.APPLY} element={<ApplyPage />} />
        <Route path={ROUTES.APPLY_QR} element={<AdmissionQrDisplayPage />} />
        <Route path={ROUTES.VERIFY_STUDENT} element={<VerifyStudentPage />} />
        <Route path={ROUTES.VERIFY_STUDENT_TOKEN} element={<VerifyStudentPage />} />
        <Route path={ROUTES.VISIT_PREREG} element={<VisitorPreRegisterPage />} />
        <Route path={ROUTES.VISIT_QR} element={<VisitorQrDisplayPage />} />
        <Route path={ROUTES.RECEIPT_PUBLIC} element={<FeeReceiptPage />} />
        <Route path={ROUTES.CERTIFICATE_PUBLIC} element={<CertificatePublicPage />} />


        {/* Super Admin */}
        <Route element={<ProtectedRoute allowedRoles={["SUPER_ADMIN"]} />}>
          <Route element={<SuperAdminLayout />}>
            <Route path={ROUTES.SUPER_ADMIN.DASHBOARD} element={<DashboardPage />} />
            <Route path={ROUTES.SUPER_ADMIN.ORG_MANAGEMENT} element={<OrgManagementPage />} />
            <Route path={ROUTES.SUPER_ADMIN.INSTITUTION_RECYCLE_BIN} element={<InstitutionRecycleBinPage />} />
            <Route path={ROUTES.SUPER_ADMIN.ORG_RECYCLE_BIN} element={<InstitutionRecycleBinPage />} />
            <Route
              path={ROUTES.SUPER_ADMIN.ORG_MANAGEMENT_DETAILS}
              element={<OrganizationDetailsPage />}
            />
            <Route
              path={ROUTES.SUPER_ADMIN.INDEPENDENT_INSTITUTION_DETAILS}
              element={<IndependentInstitutionDetailsPage />}
            />
            {/*
              Campus details sits one level deeper than the org details
              route — reached from CampusTable's "View Details" button,
              which previously did nothing but stopPropagation().
            */}
            <Route
              path={ROUTES.SUPER_ADMIN.CAMPUS_DETAILS}
              element={<CampusDetailsPage />}
            />
            <Route path={ROUTES.SUPER_ADMIN.LEAD_TRACKER} element={<LeadTrackerPage />} />
            <Route path={ROUTES.SUPER_ADMIN.INTERNAL_ROLES} element={<InternalRolesPage />} />
            <Route path={ROUTES.SUPER_ADMIN.STAFF} element={<StaffPage />} />
            <Route path={ROUTES.SUPER_ADMIN.SUBJECTS} element={<SubjectsPage />} />
            <Route path={ROUTES.SUPER_ADMIN.FEE_CATEGORIES} element={<Navigate to={ROUTES.SUPER_ADMIN.FEE_TEMPLATES} replace />} />
            <Route path={ROUTES.SUPER_ADMIN.FEE_TEMPLATES} element={<FeeTemplatesPage />} />
            <Route path={ROUTES.SUPER_ADMIN.WHATSAPP} element={<WhatsappPage />} />
            <Route path={ROUTES.SUPER_ADMIN.STUDENT_CATEGORIES} element={<StudentCategoriesPage />} />
            <Route path={ROUTES.SUPER_ADMIN.ACADEMIC_SETUP} element={<AcademicSetupPage />} />
            <Route path={ROUTES.SUPER_ADMIN.AUDIT_LOGS} element={<AuditLogsPage />} />
            <Route path={ROUTES.SUPER_ADMIN.SUBSCRIPTION_HISTORY} element={<SubscriptionHistoryPage />} />
            <Route path={ROUTES.SUPER_ADMIN.LOGIN_ACTIVITY} element={<LoginActivityPage />} />
            <Route path={ROUTES.SUPER_ADMIN.ERROR_LOGS} element={<ErrorLogsPage />} />
            <Route path={ROUTES.SUPER_ADMIN.STORAGE} element={<StoragePage />} />
            <Route path={ROUTES.SUPER_ADMIN.STORAGE_DETAILS} element={<StorageDetailsPage />} />
            <Route path={ROUTES.SUPER_ADMIN.ACADEMIC_YEARS} element={<AcademicYearsPage />} />
            <Route path={ROUTES.SUPER_ADMIN.ADMISSION_FORMS} element={<AdmissionFormsPage />} />
            <Route path={ROUTES.SUPER_ADMIN.RELIGIONS} element={<ReligionsPage />} />
            <Route path={ROUTES.SUPER_ADMIN.SIGNATURE_ROLES} element={<SignatureRolesPage />} />
            <Route path={ROUTES.SUPER_ADMIN.REPORT_TEMPLATES} element={<ReportTemplatesPage />} />
            <Route path={ROUTES.SUPER_ADMIN.CMS.HERO} element={<HeroPage />} />
            <Route path={ROUTES.SUPER_ADMIN.CMS.CHALLENGES} element={<ChallengesPage />} />
            <Route path={ROUTES.SUPER_ADMIN.CMS.PRICING} element={<PricingPage />} />
            <Route path={ROUTES.SUPER_ADMIN.CMS.TESTIMONIALS} element={<TestimonialsPage />} />
            <Route path={ROUTES.SUPER_ADMIN.CMS.FAQ} element={<FaqPage />} />
            <Route path={ROUTES.SUPER_ADMIN.CMS.SOCIAL_LINKS} element={<SocialLinksPage />} />
            <Route path={ROUTES.SUPER_ADMIN.CMS.BLOG} element={<Blog />} />
            <Route path={ROUTES.SUPER_ADMIN.CMS.PAGES} element={<CmsPages />} />
            <Route path={panelCatchAll(ROUTES.SUPER_ADMIN.DASHBOARD)} element={<NotFoundPage inPanel />} />
          </Route>
        </Route>


        {/* Institution Admin */}
        <Route element={<ProtectedRoute allowedRoles={["INSTITUTION", "ACCOUNTANT", "LIBRARIAN", "DYNAMIC"]} />}>
          <Route element={<InstitutionAdminLayout />}>
            {/* The staff workspace lives under the same layout, which shows the staff shell to staff logins, so moving between its tabs and other pages never remounts the frame. */}
            <Route path={ROUTES.STAFF_PORTAL.DASHBOARD} element={<StaffPortalPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.DASHBOARD} element={<InstitutionAdminDashboardPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.STUDENT_MANAGEMENT} element={<StudentsHubPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.STAFF_HR} element={<StaffHubPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.ROLES_PERMISSIONS} element={<RolesPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.STAFF_ID_CARDS} element={<StaffIdCardsPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.FACILITIES} element={<FacilitiesHubPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.STUDENTS} element={<WithStudentsNav active="students"><StudentsPage /></WithStudentsNav>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.ID_CARDS} element={<WithStudentsNav active="idCards"><IdCardsPage /></WithStudentsNav>} />
            <Route path={`${ROUTES.INSTITUTION_ADMIN.STUDENTS}/:studentId`} element={<StudentDetailPage />} />
            <Route path={`${ROUTES.INSTITUTION_ADMIN.STUDENTS}/:studentId/fees`} element={<StudentFeeDeskPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.EXAMS_RESULTS} element={<ExamsPage />} />
            <Route path={`${ROUTES.INSTITUTION_ADMIN.EXAMS_RESULTS}/:examId`} element={<ExamDetailPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.TEACHING_LEARNING} element={<TeachingHubPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.HOMEWORK} element={<WithTeachingNav active="homework"><HomeworkPage /></WithTeachingNav>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.NOTES} element={<WithTeachingNav active="notes"><NotesPage /></WithTeachingNav>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.CLASS_DIARY} element={<WithTeachingNav active="diary"><ClassDiaryPage /></WithTeachingNav>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.STUDENT_JOURNEY} element={<WithStudentsNav active="journey"><AdminStudentJourneyPage /></WithStudentsNav>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.STUDENT_LEAVE} element={<WithStudentsNav active="leave"><StudentLeavePage /></WithStudentsNav>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.LESSON_PLANS} element={<WithTeachingNav active="lessonPlans"><LessonPlansPage /></WithTeachingNav>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.SYLLABUS} element={<WithTeachingNav active="syllabus"><MasterSyllabusPage /></WithTeachingNav>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.COMMUNICATION} element={<CommunicationHubPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.NOTICES} element={<><CommunicationSubNav /><NoticesPage /></>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.CERTIFICATES} element={<RequireModule module="certificates"><WithStudentsNav active="certificates"><CertificatesPage /></WithStudentsNav></RequireModule>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.FINANCE_ACCOUNTING} element={<FinancePage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.PAYROLL} element={<RequireModule module="staff_payroll"><PayrollWorkspacePage /></RequireModule>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.ONLINE_LEARNING} element={<OnlineLearningPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.ALUMNI} element={<RequireModule module="alumni"><WithStudentsNav active="alumni"><AlumniPage /></WithStudentsNav></RequireModule>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.SETTINGS} element={<InstitutionSettingsPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.ACADEMICS} element={<CurriculumHubPage />} />
            <Route
              path={`${ROUTES.INSTITUTION_ADMIN.ACADEMICS}/classes`}
              element={<CurriculumFrame active="classes"><AcademicsPage /></CurriculumFrame>}
            />
            <Route
              path={`${ROUTES.INSTITUTION_ADMIN.ACADEMICS}/subjects`}
              element={<CurriculumFrame active="subjects"><SubjectsCatalogPage /></CurriculumFrame>}
            />
            <Route
              path={`${ROUTES.INSTITUTION_ADMIN.ACADEMICS}/timetable`}
              element={<CurriculumFrame active="timetable" wide><TimetablePage /></CurriculumFrame>}
            />
            <Route path={`${ROUTES.INSTITUTION_ADMIN.ACADEMICS}/class-teachers`} element={<ClassTeachersPage />} />
            <Route
              path={`${ROUTES.INSTITUTION_ADMIN.ACADEMICS}/timetable/generate`}
              element={<GenerateTimetablePage />}
            />
            <Route path={ROUTES.INSTITUTION_ADMIN.ALL_STAFF} element={<StaffPageWrapper />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.STAFF_PAYROLL} element={<StaffPageWrapper />} />
            <Route path={`${ROUTES.INSTITUTION_ADMIN.STAFF_PAYROLL}/:staffId`} element={<StaffDetailPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.TEACHERS} element={<TeacherManagementPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.DRIVERS} element={<DriverManagementPage />} />
            <Route
              path={ROUTES.INSTITUTION_ADMIN.ACCOUNTANTS}
              element={
                <RoleStaffPage
                  roleName="ACCOUNTANT"
                  title="Accountant Management"
                  description="Accountants on this institution's staff — added via the Staff module, managed here."
                  icon={Banknote}
                />
              }
            />
            <Route
              path={ROUTES.INSTITUTION_ADMIN.LIBRARIANS}
              element={
                <RoleStaffPage
                  roleName="LIBRARIAN"
                  title="Librarian Management"
                  description="Librarians on this institution's staff — added via the Staff module, managed here."
                  icon={Library}
                />
              }
            />
            <Route path={ROUTES.INSTITUTION_ADMIN.FRONT_DESK} element={<RequireModule module="visitor_management"><FrontDeskManagementPage /></RequireModule>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.FEES_PAYMENTS} element={<FeesPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.OLD_DUES} element={<InstitutionOldDuesPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.ADMISSIONS} element={<EnrollmentHubPage />} />
            <Route path={`${ROUTES.INSTITUTION_ADMIN.ADMISSIONS}/new`} element={<AdmissionFormPage forcedMode="DIRECT" />} />
            <Route path={`${ROUTES.INSTITUTION_ADMIN.ADMISSIONS}/readmission`} element={<AdmissionFormPage forcedMode="READMIT" />} />
            <Route path={`${ROUTES.INSTITUTION_ADMIN.ADMISSIONS}/transfer`} element={<AdmissionFormPage forcedMode="TRANSFER" />} />
            <Route path={`${ROUTES.INSTITUTION_ADMIN.ADMISSIONS}/inquiries`} element={<InquiriesPage forcedTab="INQUIRIES" />} />
            <Route path={`${ROUTES.INSTITUTION_ADMIN.ADMISSIONS}/requests`} element={<InquiriesPage forcedTab="REQUESTS" />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.PROMOTIONS} element={<PromotionsPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.ATTENDANCE} element={<AttendancePage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.STAFF_ATTENDANCE} element={<StaffAttendancePage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.LEAVE} element={<LeavePage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.STAFF_MEETINGS} element={<StaffMeetingsPage />} />
            <Route path={ROUTES.INSTITUTION_ADMIN.LIBRARY} element={<RequireModule module="library"><LibraryPage /></RequireModule>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.TRANSPORT} element={<RequireModule module="transport"><TransportPage /></RequireModule>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.HOSTEL} element={<RequireModule module="hostel"><HostelPage /></RequireModule>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.VISITORS} element={<RequireModule module="visitor_management"><VisitorsPage /></RequireModule>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.STORE} element={<RequireModule module="store"><StorePage /></RequireModule>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.INVENTORY} element={<RequireModule module="inventory"><InventoryPage /></RequireModule>} />
            <Route path={ROUTES.INSTITUTION_ADMIN.BILLING} element={<InstitutionAdminBillingPage />} />
            <Route path={panelCatchAll(ROUTES.INSTITUTION_ADMIN.DASHBOARD)} element={<NotFoundPage inPanel />} />
          </Route>
          {/* Receipt renders outside the admin chrome so it prints clean */}
          <Route path="/institution-admin/fee-receipt/old/:oldPaymentId" element={<FeeReceiptPage />} />
          <Route path="/institution-admin/fee-receipt/:invoiceId/:paymentId" element={<FeeReceiptPage />} />
          {/* Same reasoning — timetable print view (single class or all classes) prints clean without sidebar/header */}
          <Route path="/institution-admin/timetable-print" element={<TimetablePrintPage />} />
          {/* Same reasoning — class-wide exam result sheet, printed for the notice board */}
          <Route path="/institution-admin/exam-result-print" element={<ExamResultPrintPage />} />
          {/* Same reasoning — exam date-sheet (single class or the whole exam), printed for the notice board */}
          <Route path="/institution-admin/exam-timetable-print" element={<ExamTimetablePrintPage />} />
          {/* Same reasoning — whole-class master syllabus, every subject combined, printed plain */}
          <Route path="/institution-admin/syllabus-print" element={<SyllabusPrintPage />} />
          {/* Same reasoning — student ID card sheet (single student or a whole class), printed clean at CR80 card size */}
          <Route path="/institution-admin/id-card-print" element={<IdCardPrintPage />} />
          {/* Same reasoning — an issued certificate or letter (TC, bonafide, …), one clean A4 page */}
          <Route path="/institution-admin/certificate-print" element={<CertificatePrintPage />} />
          {/* Same reasoning — a notice or circular printed on the letterhead for the notice board or to send home */}
          <Route path="/institution-admin/notice-print" element={<NoticePrintPage />} />
        </Route>

        {/* Teacher Portal — home, timetable, class attendance (class teachers), exam marks, profile. */}
        <Route element={<ProtectedRoute allowedRoles={["TEACHER"]} />}>
          <Route element={<TeacherPortalLayout />}>
            <Route path={ROUTES.TEACHER_PORTAL.DASHBOARD} element={<TeacherHomePage />} />
            <Route path={ROUTES.TEACHER_PORTAL.TIMETABLE} element={<TeacherTimetablePage />} />
            <Route path={ROUTES.TEACHER_PORTAL.ATTENDANCE} element={<TeacherAttendancePage />} />
            <Route path={ROUTES.TEACHER_PORTAL.MARKS} element={<TeacherMarksPage />} />
            <Route path={ROUTES.TEACHER_PORTAL.HOMEWORK} element={<TeacherHomeworkPage />} />
            <Route path={ROUTES.TEACHER_PORTAL.NOTES} element={<TeacherNotesPage />} />
            <Route path={ROUTES.TEACHER_PORTAL.ONLINE_CLASSES} element={<TeacherOnlineClassesPage />} />
            <Route path={ROUTES.TEACHER_PORTAL.LESSON_PLANS} element={<TeacherLessonPlansPage />} />
            <Route path={ROUTES.TEACHER_PORTAL.CLASS_DIARY} element={<TeacherClassDiaryPage />} />
            <Route path={ROUTES.TEACHER_PORTAL.STUDENT_JOURNEY} element={<TeacherStudentJourneyPage />} />
            <Route path={ROUTES.TEACHER_PORTAL.STUDENT_LEAVE} element={<TeacherStudentLeavePage />} />
            <Route path={ROUTES.TEACHER_PORTAL.STAFF_MEETINGS} element={<TeacherStaffMeetingsPage />} />
            <Route path={ROUTES.TEACHER_PORTAL.SYLLABUS} element={<TeacherSyllabusPage />} />
            <Route path={ROUTES.TEACHER_PORTAL.PROFILE} element={<TeacherProfilePage />} />
            <Route path={panelCatchAll(ROUTES.TEACHER_PORTAL.DASHBOARD)} element={<NotFoundPage inPanel />} />
          </Route>
        </Route>

        {/* Student Portal — home, timetable, attendance, fees, results (+ report card PDF), profile. */}
        <Route element={<ProtectedRoute allowedRoles={["STUDENT"]} />}>
          <Route element={<StudentPortalLayout />}>
            <Route path={ROUTES.STUDENT_PORTAL.DASHBOARD} element={<StudentHomePage />} />
            <Route path={ROUTES.STUDENT_PORTAL.TIMETABLE} element={<StudentTimetablePage />} />
            <Route path={ROUTES.STUDENT_PORTAL.ATTENDANCE} element={<StudentAttendancePage />} />
            <Route path={ROUTES.STUDENT_PORTAL.FEES} element={<StudentFeesPage />} />
            <Route path={ROUTES.STUDENT_PORTAL.EXAMS} element={<StudentExamsPage />} />
            <Route path={ROUTES.STUDENT_PORTAL.RESULTS} element={<StudentResultsPage />} />
            <Route path={ROUTES.STUDENT_PORTAL.HOMEWORK} element={<StudentHomeworkPage />} />
            <Route path={ROUTES.STUDENT_PORTAL.NOTES} element={<StudentNotesPage />} />
            <Route path={ROUTES.STUDENT_PORTAL.DIARY} element={<StudentDiaryPage />} />
            <Route path={ROUTES.STUDENT_PORTAL.LEAVE} element={<StudentPortalLeavePage />} />
            <Route path={ROUTES.STUDENT_PORTAL.ONLINE_CLASSES} element={<StudentOnlineClassesPage />} />
            <Route path={ROUTES.STUDENT_PORTAL.SYLLABUS} element={<StudentSyllabusPage />} />
            <Route path={ROUTES.STUDENT_PORTAL.PROFILE} element={<StudentProfilePage />} />
            <Route path={panelCatchAll(ROUTES.STUDENT_PORTAL.DASHBOARD)} element={<NotFoundPage inPanel />} />
          </Route>
        </Route>

        {/* Org Admin */}
        <Route element={<ProtectedRoute allowedRoles={["ORG"]} />}>
          <Route element={<OrgAdminLayout />}>
            <Route path={ROUTES.ORG_ADMIN.DASHBOARD} element={<OrgAdminDashboardPage />} />
            <Route path={ROUTES.ORG_ADMIN.SHARED_STAFF} element={<OrgAdminSharedStaffPage />} />
            <Route
              path={ROUTES.ORG_ADMIN.DRIVERS}
              element={<OrgAdminSharedStaffPage roleFilter="DRIVER" title="Driver Management" description="Drivers shared across this organization's institutes." />}
            />
            <Route
              path={ROUTES.ORG_ADMIN.ACCOUNTANTS}
              element={<OrgAdminSharedStaffPage roleFilter="ACCOUNTANT" title="Accountant Management" description="Accountants shared across this organization's institutes." />}
            />
            <Route
              path={ROUTES.ORG_ADMIN.LIBRARIANS}
              element={<OrgAdminSharedStaffPage roleFilter="LIBRARIAN" title="Librarian Management" description="Librarians shared across this organization's institutes." />}
            />
            <Route path={ROUTES.ORG_ADMIN.BILLING} element={<OrgAdminBillingPage />} />
            <Route
              path={ROUTES.ORG_ADMIN.SETTINGS}
              element={<ComingSoon title="Settings" description="Configure organization-level preferences." />}
            />
            <Route path={panelCatchAll(ROUTES.ORG_ADMIN.DASHBOARD)} element={<NotFoundPage inPanel />} />
          </Route>
        </Route>

        {/* Campus Admin */}
        <Route element={<ProtectedRoute allowedRoles={["CAMPUS"]} />}>
          <Route element={<CampusAdminLayout />}>
            <Route path={ROUTES.CAMPUS_ADMIN.DASHBOARD} element={<CampusAdminDashboardPage />} />
            <Route
              path={ROUTES.CAMPUS_ADMIN.INSTITUTIONS}
              element={<ComingSoon title="Institutions" description="Manage institutions under this campus." />}
            />
            <Route
              path={ROUTES.CAMPUS_ADMIN.STUDENTS}
              element={<ComingSoon title="Students" description="Manage student records across this campus." />}
            />
            <Route
              path={ROUTES.CAMPUS_ADMIN.ATTENDANCE}
              element={<ComingSoon title="Attendance" description="Track student and staff attendance for this campus." />}
            />
            <Route path={ROUTES.CAMPUS_ADMIN.STAFF} element={<CampusAdminSharedStaffPage />} />
            <Route
              path={ROUTES.CAMPUS_ADMIN.DRIVERS}
              element={<CampusAdminSharedStaffPage roleFilter="DRIVER" title="Driver Management" description="Drivers shared across this campus's institutes." />}
            />
            <Route
              path={ROUTES.CAMPUS_ADMIN.ACCOUNTANTS}
              element={<CampusAdminSharedStaffPage roleFilter="ACCOUNTANT" title="Accountant Management" description="Accountants shared across this campus's institutes." />}
            />
            <Route
              path={ROUTES.CAMPUS_ADMIN.LIBRARIANS}
              element={<CampusAdminSharedStaffPage roleFilter="LIBRARIAN" title="Librarian Management" description="Librarians shared across this campus's institutes." />}
            />
            <Route
              path={ROUTES.CAMPUS_ADMIN.FEES}
              element={<ComingSoon title="Fees" description="Track fee collection across this campus." />}
            />
            <Route
              path={ROUTES.CAMPUS_ADMIN.REPORTS}
              element={<ComingSoon title="Reports" description="View campus-wide reports and analytics." />}
            />
            <Route path={panelCatchAll(ROUTES.CAMPUS_ADMIN.DASHBOARD)} element={<NotFoundPage inPanel />} />
          </Route>
        </Route>

        {/* Catch-all — any URL no route matched (outside the panels, which have their own above) shows the 404 page */}
        <Route path="/pages" element={<CmsPagesList />} />
        <Route path="/:slug" element={<CmsPageView />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}